import { useEffect, useState } from 'react'
import { ChevronsUpDown, UserPlus } from 'lucide-react'
import { api, type UserRef } from '@/api/client'
import { searchDirectory } from '@/auth/graph'
import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Spinner } from '@/components/ui/spinner'
import { UserPhoto } from '@/components/UserPhoto'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { initialsOf } from '../../components/AvatarStack'

export interface PickedPerson { id: string; name: string; email: string }

interface Props {
    onSelect: (p: PickedPerson) => void
    /** Currently selected person (select mode) - shown on the trigger. */
    value?: PickedPerson | null
    /** Hide people already chosen. */
    exclude?: string[]
    placeholder?: string
    /** "add" renders an outline button with a plus icon; "select" a combobox-style trigger. */
    mode?: 'add' | 'select'
    'aria-label'?: string
}

/** Directory search (>= 2 chars, debounced) via Graph (falls back to the local users table). Returns {id,name,email}. */
export function PersonPicker({ onSelect, value, exclude = [], placeholder = 'Search for a person…', mode = 'select', ...rest }: Props) {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [items, setItems] = useState<UserRef[]>([])
    const [loading, setLoading] = useState(false)
    const [failed, setFailed] = useState(false)
    const term = query.trim()

    useEffect(() => {
        if (term.length < 2) { setItems([]); setLoading(false); setFailed(false); return }
        let cancelled = false
        setLoading(true)
        const t = setTimeout(() => {
            searchDirectory(term)
                .then((r) => { if (!cancelled) { setItems(r); setFailed(false) } })
                .catch(() => { if (!cancelled) { setItems([]); setFailed(true) } })
                .finally(() => { if (!cancelled) setLoading(false) })
        }, 250)
        return () => { cancelled = true; clearTimeout(t) }
    }, [term])

    const visible = items.filter((u) => !exclude.includes(u.id))

    return (
        <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery('') }}>
            <PopoverTrigger asChild>
                {mode === 'add' ? (
                    <Button type="button" variant="outline" size="sm" aria-label={rest['aria-label'] ?? placeholder}>
                        <UserPlus data-icon="inline-start" />
                        {placeholder}
                    </Button>
                ) : (
                    <Button type="button" variant="outline" role="combobox" aria-expanded={open}
                        aria-label={rest['aria-label'] ?? placeholder}
                        className="w-full justify-between font-normal sm:w-72">
                        <span className={value ? 'truncate' : 'truncate text-muted-foreground'}>{value?.name ?? placeholder}</span>
                        <ChevronsUpDown data-icon="inline-end" />
                    </Button>
                )}
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start">
                <Command shouldFilter={false}>
                    <CommandInput placeholder="Type a name or email…" value={query} onValueChange={setQuery} />
                    <CommandList>
                        {loading && (
                            <div className="flex items-center gap-2 px-3 py-6 text-sm text-muted-foreground">
                                <Spinner /> Searching…
                            </div>
                        )}
                        {!loading && term.length < 2 && <div className="px-3 py-6 text-sm text-muted-foreground">Type at least 2 characters.</div>}
                        {!loading && failed && <div className="px-3 py-6 text-sm text-destructive">Search failed. Try again.</div>}
                        {!loading && !failed && term.length >= 2 && visible.length === 0 && <CommandEmpty>No people found.</CommandEmpty>}
                        {visible.length > 0 && (
                            <CommandGroup>
                                {visible.map((u) => (
                                    <CommandItem key={u.id} value={u.id}
                                        onSelect={() => {
                                            // Make sure the backend knows this person (they may never have signed in).
                                            void api.cacheUser(u).catch(() => undefined)
                                            onSelect({ id: u.id, name: u.name, email: u.email }); setOpen(false); setQuery('')
                                        }}>
                                        <Avatar className="size-6">
                                            <UserPhoto id={u.id} />
                                            <AvatarFallback className="bg-primary/10 text-[10px] font-semibold text-primary">{initialsOf(u.name)}</AvatarFallback>
                                        </Avatar>
                                        <div className="flex min-w-0 flex-col">
                                            <span className="truncate">{u.name}</span>
                                            {u.email && <span className="truncate text-xs text-muted-foreground">{u.email}</span>}
                                        </div>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}
